import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CachedValuesService, PermissionService } from '@services/index';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormGroup } from '@angular/forms';
import { AgreementSharedInformationForm } from '../../shared-agreement.model';
import { DropdownItem } from '@shared/components/dropdown/dropdown.component';
import { Counterpart, Customer, User } from 'app/types';
import { Observable, map, tap } from 'rxjs';
import { Permission } from 'app/enums';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BaseHighlightComponent } from '@shared/components/base-highlight/base-highlight.component';
import { AgreementMetadataGenerationService } from '../../../pages/agreement-metadata-generation.helper';

@UntilDestroy()
@Component({
    selector: 'app-agreement-parts',
    templateUrl: './agreement-parts.component.html',
    styleUrl: './agreement-parts.component.scss'
})
export class AgreementPartsComponent extends BaseHighlightComponent implements OnInit, OnDestroy {
    @Input() public detailsForm: FormGroup<AgreementSharedInformationForm>;
    @Input() public showPotentialValidationMessages: boolean;

    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly permissionService = inject(PermissionService);
    private readonly modalService = inject(NgbModal);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly metadataService = inject(AgreementMetadataGenerationService);

    public permissions = Permission;
    public userPermissions: Permission[] = this.permissionService.userPermissions;
    public selectedCounterpart: Counterpart;
    public initialCounterpartCompanyName: string;

    public readonly readers$: Observable<MultiSelectItem[]> = this.cachedValuesService.users$.pipe(
        map((data) =>
            data.map((item: User) => {
                return {
                    id: item.id,
                    name: item.firstName + ' ' + item.lastName // + ' - ' + item.email
                } as MultiSelectItem;
            })
        )
    );

    private initialLoad = true;
    public readonly customers$: Observable<DropdownItem<string>[]> = this.cachedValuesService.customers$.pipe(
        map((data) =>
            data.map((item: Customer) => {
                return {
                    value: item.id,
                    label: item.name
                } as DropdownItem<string>;
            })
        ),
        tap(() => (this.initialLoad = false))
    );

    public readonly users$: Observable<DropdownItem<string>[]> = this.cachedValuesService.users$.pipe(
        map((data) =>
            data.map((item: User) => {
                return {
                    value: item.id,
                    label: item.firstName + ' ' + item.lastName
                } as DropdownItem<string>;
            })
        ),
        tap(() => (this.initialLoad = false))
    );

    public counterparts$: Observable<DropdownItem<string>[]>;

    public showPartsInfo = false;
    public get f() {
        return this.detailsForm.controls;
    }

    public get disabled() {
        return this.f.counterpartId.disabled;
    }

    public override ngOnInit() {
        super.ngOnInit();
        this.loadCounterparts();

        // Subscribe to pending counterpart names from metadata service
        this.metadataService
            .getPendingCounterpartName$()
            .pipe(untilDestroyed(this))
            .subscribe((pendingName) => {
                if (pendingName) {
                    // Open the counterpart creation form with the pending name
                    this.createCounterpartWithName(pendingName);
                    // Clear the pending name from the service
                    this.metadataService.clearPendingCounterpartName();
                }
            });
    }

    public override ngOnDestroy() {
        super.ngOnDestroy();
    }

    public toggleReaderAccess(checked: boolean) {
        if (checked) {
            //this.f.readers.disable();

            this.detailsForm.controls.readers.disable();
        } else {
            this.f.readers.enable();
        }
    }
    private loadCounterparts() {
        this.counterparts$ = this.cachedValuesService.counterparts$.pipe(
            map((data) =>
                data.map((item: Counterpart) => {
                    return {
                        value: item.id,
                        label: item.companyName
                    } as DropdownItem<string>;
                })
            ),
            tap(() => {
                this.loadCounterPartInfo(this.f.counterpartId.value);
                this.f.counterpartId.valueChanges.subscribe((counterpartId) => {
                    if (counterpartId) {
                        this.loadCounterPartInfo(counterpartId);
                    }
                });
            })
        );
    }

    private loadCounterPartInfo(counterpartId: string) {
        this.cachedValuesService.counterparts$
            .pipe(
                map((result) => {
                    return result.filter((p) => p.id == counterpartId).at(0);
                })
            )
            .subscribe({
                next: (response: Counterpart) => {
                    this.changeDetectorRef.markForCheck();
                    this.selectedCounterpart = response;
                }
            });
    }
    public createCounterpart() {
        this.selectedCounterpart = null;
        this.initialCounterpartCompanyName = '';
        this.f.counterpartId.setValue(null);
        this.showPartsInfo = true;
    }

    public createCounterpartWithName(companyName: string) {
        this.selectedCounterpart = null;
        this.initialCounterpartCompanyName = companyName;
        this.f.counterpartId.setValue(null);
        this.showPartsInfo = true;
    }

    public selectCounterpart(id: string) {
        this.cachedValuesService.invalidateCache();
        this.loadCounterparts();
        this.f.counterpartId.setValue(id);
    }
}
